import React from 'react';
import { Space, Table, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import './InvoiceTables.css';
import type { TFunction } from 'i18next';
import type { InvoiceCoverage, InvoiceCoverageInvoice, InvoiceCoverageLine } from '../../../types/invoiceCoverage';
import { coverageMarkerLabel, formatCoverageQuantity, formatRecognitionMonth } from '../invoiceCoverageLabels';
import ProductNameWithDimensions from '../../../components/products/ProductNameWithDimensions';

/**
 * WP11: coverage marker of a ZK/ZD as a Tag — „Zafakturowane częściowo (x %)”
 * or „Zafakturowane w całości” („Rozliczone fakturą rozliczeniową” for a ZK
 * settled by its settlement invoice); nothing for NONE or unknown coverage.
 */
export function InvoiceCoverageTag({ coverage }: { coverage: InvoiceCoverage | null | undefined }) {
  const { t } = useTranslation('invoices');
  const label = coverageMarkerLabel(coverage, t);
  if (!label) return null;
  return <Tag color={coverage?.state === 'FULL' || coverage?.settledBySettlement ? 'green' : 'geekblue'} data-testid="invoice-coverage-tag">{label}</Tag>;
}

function invoiceLabel(invoice: InvoiceCoverageInvoice, t: TFunction): string {
  const number = invoice.number || t('invoices:workflow.coverage.unnumbered');
  const details = [formatRecognitionMonth(invoice.recognitionMonth), invoice.corrected ? t('invoices:workflow.coverage.corrected') : null].filter(Boolean);
  return details.length ? `${number} (${details.join(', ')})` : number;
}

// „125 szt. · FS/1/2026” — which invoice covers how much of the line; the amount comes first so
// a long invoice number cut with „…” never hides it.
function lineInvoices(line: InvoiceCoverageLine, invoices: InvoiceCoverageInvoice[], t: TFunction): React.ReactNode {
  if (!line.invoicedBy?.length) return '—';
  const byId = new Map(invoices.map((invoice) => [invoice.id, invoice]));
  return <div data-testid="invoice-coverage-line-invoices">
    {line.invoicedBy.map((share) => {
      const invoice = byId.get(share.invoiceId);
      const text = `${formatCoverageQuantity(share.quantity, line.unit)} · ${invoice?.number || t('invoices:workflow.coverage.unnumbered')}`;
      return <div key={share.invoiceId} className="invoice-table__one-line" title={text}>{text}</div>;
    })}
  </div>;
}

/**
 * WP11: the order's active invoices (number, recognition month) and the
 * invoiced / remaining quantity of every line, with the quantity each invoice
 * covers when the reader may see sales invoices (`invoicedBy`). Quantities
 * only — the backend never sends money here. `showInvoiceList` false: the
 * card lists the invoices itself (ZK „Faktury sprzedaży” table).
 */
export default function InvoiceCoverageSummary({ coverage, showInvoiceList = true }: {
  coverage: InvoiceCoverage | null | undefined;
  showInvoiceList?: boolean;
}) {
  const { t } = useTranslation('invoices');
  if (!coverage || coverage.state === 'NONE') return null;
  const showLineInvoices = coverage.invoices.length > 0 && coverage.lines.some((line) => line.invoicedBy !== undefined);
  // Fixed layout with the columns as shares of the table, so „Pozycja” keeps
  // its width down to 1024 px (fixed px widths summed past the tile there and
  // the name ran over „Zamówiono”, staging v3.27.0). The name is one line with
  // „…” and the full text in `title`; a header too narrow ends with „…” and
  // carries its full text in `title` (InvoiceTables.css).
  const header = (label: string) => ({ title: label, onHeaderCell: () => ({ title: label }) });
  return <Space direction="vertical" size={12} style={{ width: '100%' }} data-testid="invoice-coverage-summary">
    {showInvoiceList && coverage.invoices.length > 0 && <Typography.Text type="secondary" data-testid="invoice-coverage-invoices">
      {t('invoices:workflow.coverage.invoices', { invoices: coverage.invoices.map((invoice) => invoiceLabel(invoice, t)).join(', ') })}
    </Typography.Text>}
    <Table<InvoiceCoverageLine>
      className="invoice-table"
      tableLayout="fixed"
      rowKey="id"
      size="small"
      pagination={false}
      dataSource={coverage.lines}
      columns={[
        {
          ...header(t('invoices:workflow.coverage.line')),
          render: (_, line) => <ProductNameWithDimensions
            name={<span className="invoice-table__one-line" title={line.name || undefined}>{line.name || '—'}</span>}
            dimensions={line}
            dimensionsTestId={`invoice-coverage-line-dimensions-${line.id}`}
          />,
        },
        { ...header(t('invoices:workflow.coverage.ordered')), align: 'right', width: '13%', render: (_, line) => formatCoverageQuantity(line.orderedQuantity, line.unit) },
        { ...header(t('invoices:workflow.coverage.invoiced')), align: 'right', width: '15%', render: (_, line) => formatCoverageQuantity(line.invoicedQuantity, line.unit) },
        { ...header(t('invoices:workflow.coverage.remaining')), align: 'right', width: '18%', render: (_, line) => formatCoverageQuantity(line.remainingQuantity, line.unit) },
        ...(showLineInvoices ? [{
          ...header(t('invoices:workflow.coverage.lineInvoices')),
          align: 'right' as const,
          width: '20%',
          render: (_: unknown, line: InvoiceCoverageLine) => lineInvoices(line, coverage.invoices, t),
        }] : []),
        // WP15: complained quantity (already taken off the line by the complaint).
        ...(coverage.lines.some((line) => line.complainedQuantity != null) ? [{
          ...header(t('invoices:workflow.coverage.complained')),
          align: 'right' as const,
          width: '14%',
          render: (_: unknown, line: InvoiceCoverageLine) => (line.complainedQuantity != null
            ? <span data-testid="invoice-coverage-complained">{formatCoverageQuantity(line.complainedQuantity, line.unit)}</span>
            : '—'),
        }] : []),
      ]}
    />
  </Space>;
}
